/**
 * DMS Attachments TanStack Query hooks.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as api from '../api';
import type { AttachmentEntityType } from '../types';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

export const attachmentKeys = {
  all: ['attachments'] as const,
  list: (entityType: AttachmentEntityType, entityId: string) =>
    [...attachmentKeys.all, 'list', entityType, entityId] as const,
};

export function useAttachments(entityType: AttachmentEntityType, entityId: string) {
  return useQuery({
    queryKey: attachmentKeys.list(entityType, entityId),
    queryFn: () => api.getAttachments(entityType, entityId),
    enabled: !!entityType && !!entityId,
  });
}

export function useCreateAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      entity_type: AttachmentEntityType;
      entity_id: string;
      document_id: string;
    }) => api.createAttachment(data),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({
        queryKey: attachmentKeys.list(variables.entity_type, variables.entity_id),
      });
      toast.success('Attachment added');
    },
    onError: (e) => toast.error('Failed to add attachment', { description: getErrorMessage(e) }),
  });
}

export function useDeleteAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      entityType: _entityType,
      entityId: _entityId,
    }: {
      id: string;
      entityType: AttachmentEntityType;
      entityId: string;
    }) => api.deleteAttachment(id),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({
        queryKey: attachmentKeys.list(variables.entityType, variables.entityId),
      });
      toast.success('Attachment removed');
    },
    onError: (e) =>
      toast.error('Failed to remove attachment', { description: getErrorMessage(e) }),
  });
}
